import { useCallback, useEffect, useRef, useState } from "react";

import {
  apiListAllUsers,
  apiSearchUsers,
  type SearchedUser,
} from "../data/api";

import "./UserPicker.scss";

type Props = {
  value: SearchedUser[];
  onChange: (users: SearchedUser[]) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Mostra um botão "Selecionar todos" que adiciona todos os usuários internos. */
  allowSelectAll?: boolean;
};

const SEARCH_DEBOUNCE_MS = 250;

function userLabel(u: SearchedUser): string {
  return u.name ? `${u.name} (${u.email})` : u.email;
}

export const UserPicker = ({
  value,
  onChange,
  disabled,
  placeholder = "Buscar por nome ou e-mail...",
  allowSelectAll,
}: Props) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce: refaz a busca 250ms depois da última tecla.
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = window.setTimeout(async () => {
      try {
        const users = await apiSearchUsers(query);
        // Filtra usuários já selecionados.
        const selectedIds = new Set(value.map((u) => u.id));
        setResults(users.filter((u) => !selectedIds.has(u.id)));
      } catch (err) {
        console.error("user search failed:", err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query, value]);

  // Fecha o dropdown ao clicar fora.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const addUser = useCallback(
    (user: SearchedUser) => {
      onChange([...value, user]);
      setQuery("");
      setResults([]);
      inputRef.current?.focus();
    },
    [value, onChange],
  );

  const removeUser = useCallback(
    (userId: string) => {
      onChange(value.filter((u) => u.id !== userId));
    },
    [value, onChange],
  );

  const selectAll = useCallback(async () => {
    setSelectingAll(true);
    try {
      const users = await apiListAllUsers();
      onChange(users);
      setQuery("");
      setResults([]);
    } catch (err) {
      console.error("select all users failed:", err);
    } finally {
      setSelectingAll(false);
    }
  }, [onChange]);

  return (
    <div className="UserPicker" ref={containerRef}>
      {allowSelectAll && (
        <div className="UserPicker__actions">
          <button
            type="button"
            className="UserPicker__selectAll"
            onClick={selectAll}
            disabled={disabled || selectingAll}
          >
            {selectingAll ? "Selecionando..." : "Selecionar todos"}
          </button>
        </div>
      )}
      <div className="UserPicker__field">
        {value.map((u) => (
          <span key={u.id} className="UserPicker__chip">
            {userLabel(u)}
            <button
              type="button"
              className="UserPicker__chipRemove"
              onClick={() => removeUser(u.id)}
              disabled={disabled}
              aria-label={`Remover ${userLabel(u)}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          className="UserPicker__input"
          value={query}
          placeholder={value.length === 0 ? placeholder : ""}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          disabled={disabled}
        />
      </div>

      {open && (query.trim() || loading) && (
        <div className="UserPicker__dropdown">
          {loading && <div className="UserPicker__hint">Buscando...</div>}
          {!loading && results.length === 0 && query.trim() && (
            <div className="UserPicker__hint">Nenhum usuário encontrado.</div>
          )}
          {!loading &&
            results.map((u) => (
              <button
                key={u.id}
                type="button"
                className="UserPicker__option"
                onClick={() => addUser(u)}
              >
                <span className="UserPicker__optionName">
                  {u.name || u.email}
                </span>
                {u.name && (
                  <span className="UserPicker__optionEmail">{u.email}</span>
                )}
              </button>
            ))}
        </div>
      )}
    </div>
  );
};
